/**
 * 달력의 하루
 * ---------------------------------------------------------------------------
 * 날짜를 누르면 뜬다. 그날 쓴 일지는 열고, 안 쓴 일지는 바로 쓰러 간다.
 *
 * 날씨를 여기서 먼저 보여 준다
 *   작성 화면으로 가면 어차피 자동으로 채워지지만, 며칠 전 날짜를 채우러
 *   왔을 때 "그날 비가 왔었나" 를 가기 전에 확인할 수 있다. 사업장 주소지
 *   기준 값이다.
 *
 * 작성 화면으로 날짜와 사업장을 넘긴다
 *   오늘로 열리면 사용자는 달력에서 고른 날을 작성 화면에서 다시 찾아야 한다.
 *
 * 오늘 이후
 *   아직 오지 않은 날의 일지는 쓰지 않는다. 쓸 수 있게 두면 미리 채워 두고
 *   그날의 사실과 다른 기록이 남는다.
 */

import { useQuery } from '@tanstack/react-query';
import { Button, Divider, Empty, List, Modal, Space, Spin, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import type { CalendarDay, LogType, WeatherReading } from '@eco/shared';
import { LOG_STATUS_LABELS, LOG_TYPE_LABELS, LOG_TYPE_MENU } from '@eco/shared';
import { api } from '../../lib/api';
import { WeatherIcon } from '../../components/WeatherIcon';
import { LOG_TYPE_COLOR, LOG_TYPE_ICON } from '../../components/logTypeVisual';

const { Text, Title } = Typography;

export function DayDialog({
  day,
  siteId,
  enabledTypes,
  canWrite,
  onClose,
}: {
  day: CalendarDay | null;
  siteId: string | undefined;
  enabledTypes: LogType[];
  canWrite: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const date = day?.date;

  const weather = useQuery<WeatherReading | null>({
    queryKey: ['weather', siteId, date],
    queryFn: async () =>
      (
        await api.get<WeatherReading | null>('/daily-logs/weather/lookup', {
          params: { siteId, date },
        })
      ).data,
    enabled: !!day && !!siteId,
    // 지난 날의 날씨는 바뀌지 않는다.
    staleTime: 30 * 60 * 1000,
  });

  if (!day) return null;

  const isFuture = day.date > dayjs().format('YYYY-MM-DD');
  const written = new Set(day.logs.map((l) => l.logType));
  const writable = enabledTypes.filter((t) => !written.has(t));

  const goWrite = (type: LogType) => {
    const params = new URLSearchParams({ date: day.date });
    if (siteId) params.set('siteId', siteId);
    onClose();
    navigate(`/logs/${type}/new?${params.toString()}`);
  };

  const goOpen = (type: LogType, dailyLogId: string) => {
    onClose();
    navigate(`/logs/${type}/${dailyLogId}`);
  };

  const label = dayjs(day.date).format('YYYY년 M월 D일 (dd)');

  return (
    <Modal
      open
      onCancel={onClose}
      footer={<Button onClick={onClose}>닫기</Button>}
      title={
        <Space wrap>
          <span>{label}</span>
          {day.holidays.map((h) => (
            <Tag key={h.name} color="red">
              {h.name}
            </Tag>
          ))}
        </Space>
      }
      width={520}
    >
      {/* 날씨 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 12px',
          background: '#fafafa',
          borderRadius: 8,
          marginBottom: 16,
        }}
      >
        {weather.isLoading ? (
          <Spin size="small" />
        ) : weather.data ? (
          <>
            <WeatherIcon code={weather.data.weatherCode} label={weather.data.weather} size={28} />
            <div>
              <Text strong>{weather.data.weather}</Text>
              {weather.data.temperature !== null && (
                <Text style={{ marginLeft: 8 }}>{weather.data.temperature}℃</Text>
              )}
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  사업장 주소지 기준 · 작성 화면에 자동으로 채워집니다
                </Text>
              </div>
            </div>
          </>
        ) : (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {siteId
              ? '이 날짜의 날씨를 가져오지 못했습니다. 작성 화면에서 직접 입력할 수 있습니다.'
              : '사업장을 고르면 날씨를 보여 드립니다.'}
          </Text>
        )}
      </div>

      {/* 작성한 일지 */}
      {day.logs.length > 0 && (
        <>
          <Title level={5} style={{ marginTop: 0 }}>
            작성한 일지
          </Title>
          <List
            size="small"
            dataSource={day.logs}
            renderItem={(log) => (
              <List.Item
                actions={[
                  <Button key="open" size="small" onClick={() => goOpen(log.logType, log.dailyLogId)}>
                    열기
                  </Button>,
                ]}
              >
                <Space>
                  <span style={{ color: LOG_TYPE_COLOR[log.logType] }}>
                    {LOG_TYPE_ICON[log.logType]}
                  </span>
                  <span>{LOG_TYPE_LABELS[log.logType]}</span>
                  <Tag color={log.status === 'CONFIRMED' ? 'green' : 'blue'}>
                    {LOG_STATUS_LABELS[log.status]}
                  </Tag>
                </Space>
              </List.Item>
            )}
          />
        </>
      )}

      {/* 작성하러 가기 */}
      {isFuture ? (
        <>
          {day.logs.length > 0 && <Divider style={{ margin: '12px 0' }} />}
          <Text type="secondary">아직 오지 않은 날입니다. 그날이 되면 작성할 수 있습니다.</Text>
        </>
      ) : writable.length > 0 && canWrite ? (
        <>
          {day.logs.length > 0 && <Divider style={{ margin: '12px 0' }} />}
          <Title level={5} style={{ marginTop: 0 }}>
            등록 화면으로 이동하시겠습니까?
          </Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
            작성할 일지를 고르면 이 날짜로 등록 화면이 열립니다.
          </Text>
          <Space direction="vertical" style={{ width: '100%' }}>
            {writable.map((type) => (
              <Button
                key={type}
                block
                size="large"
                icon={<span style={{ color: LOG_TYPE_COLOR[type] }}>{LOG_TYPE_ICON[type]}</span>}
                onClick={() => goWrite(type)}
                style={{ textAlign: 'left' }}
              >
                {LOG_TYPE_MENU[type]} 작성
                {day.missing.includes(type) && (
                  <Tag style={{ marginLeft: 8 }}>미작성</Tag>
                )}
              </Button>
            ))}
          </Space>
        </>
      ) : day.logs.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={canWrite ? '대시보드에 켜 둔 일지가 없습니다.' : '작성 권한이 없습니다.'}
        />
      ) : null}
    </Modal>
  );
}
